/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2026 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl, createGuestCart } from '@/cart/api';
import { state } from '@/cart/lib/state';
import { CartModel } from '@/cart/data/models';
import { transformCart } from '@/cart/data/transforms';
import { events } from '@adobe-commerce/event-bus';
import { handleFetchError } from '@/cart/lib/fetch-error';
import { getPersistedCartData } from '@/cart/lib/persisted-data';
import { publishCartUpdateEvents } from '@/cart/lib/acdl';
import { SELECT_FREE_GIFT_FOR_CART_MUTATION } from './graphql/SelectFreeGiftForCartMutation';

export type SelectFreeGiftForCartInput = {
  ruleId: number;
  sku: string;
  quantity?: number;
  enteredOptions?: { uid: string; value: string }[];
  /**
   * Configurable: configurable option value UIDs.
   * Bundle: one `BundleItemOption.uid` per bundle slot (same as `addProductsToCart` `selected_options`).
   */
  selectedOptions?: string[];
};

/**
 * Persists a free-gift choice for one rule via `selectFreeGiftForCart`
 * (`Magento_SalesRuleFreeGift`).
 */
export const selectFreeGiftForCart = async ({
  ruleId,
  sku,
  quantity,
  enteredOptions,
  selectedOptions,
}: SelectFreeGiftForCartInput): Promise<CartModel | null> => {
  const cartId =
    state.cartId ||
    (await createGuestCart().then((id) => {
      return id;
    }));

  const input: Record<string, unknown> = {
    cart_id: cartId,
    rule_id: ruleId,
    sku,
  };

  if (quantity != null) input.quantity = quantity;
  if (enteredOptions?.length) {
    input.entered_options = enteredOptions.map(({ uid, value }) => ({
      uid,
      value,
    }));
  }
  if (selectedOptions?.length) input.selected_options = selectedOptions;

  return fetchGraphQl(SELECT_FREE_GIFT_FOR_CART_MUTATION, {
    variables: { input },
  }).then(({ errors, data }) => {
    const _errors = [...(errors ?? [])];

    if (_errors.length > 0) return handleFetchError(_errors);

    const cartPayload = data?.selectFreeGiftForCart?.cart;
    if (!cartPayload) {
      throw new Error('selectFreeGiftForCart did not return a cart');
    }

    const payload = transformCart(cartPayload);

    const previousCart = getPersistedCartData();
    const previousItems = previousCart?.items || [];

    events.emit('cart/updated', payload);
    events.emit('cart/data', payload);

    if (payload) {
      const newItems = payload.items.filter(
        (item) => !previousItems.some((prev) => prev.uid === item.uid)
      );
      if (newItems.length > 0) {
        events.emit('cart/product/added', newItems);
      }

      publishCartUpdateEvents(
        payload,
        payload.items.filter(
          (item) => item.sku === sku || item.topLevelSku === sku
        ),
        state.locale ?? 'en-US'
      );
    }

    return payload;
  });
};
